/**
 * Generated by orval v7.17.2 🍺
 * Do not edit manually.
 * Affiliate Postback Receiver API
 * Production-ready S2S postback ingestion and querying service for affiliate marketing platforms.

## Features
- **Postback Ingestion**: Accept affiliate conversion events via GET/POST webhooks
- **Deduplication**: Automatic duplicate detection to prevent double counting
- **Event Normalization**: Standardize events from multiple affiliate networks
- **Query API**: Search and filter historical events with pagination
- **Analytics**: Statistics aggregations and CSV export for reporting
- **Health Checks**: Kubernetes-ready liveness and readiness probes
- **Metrics**: Prometheus-compatible metrics for monitoring

## Supported Networks
- Referon (via `/postbacks/referon` endpoint)

## Event Types
- **CLICK**: User clicked on affiliate link
- **REG**: User registration
- **FTD**: First-time deposit
- **DEP**: Regular deposit
- **REDEP**: Repeat deposit
 * OpenAPI spec version: 1.0.0
 */
import { useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  AnalyticsHealthResponseDto,
  EventsControllerCompareStatsParams,
  EventsControllerExportCsvParams,
  EventsControllerGetAnalyticsHealthParams,
  EventsControllerGetStatsParams,
  EventsControllerQueryEventsParams,
  EventsResponseDto,
} from '.././model';

import { customFetch } from '../../../shared/api/custom-client';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Search and filter affiliate events with pagination support
 * @summary Query affiliate events
 */
export type eventsControllerQueryEventsResponse200 = {
  data: EventsResponseDto;
  status: 200;
};

export type eventsControllerQueryEventsResponseSuccess = eventsControllerQueryEventsResponse200 & {
  headers: Headers;
};
export type eventsControllerQueryEventsResponse = eventsControllerQueryEventsResponseSuccess;

export const getEventsControllerQueryEventsUrl = (params?: EventsControllerQueryEventsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/events?${stringifiedParams}` : `/events`;
};

export const eventsControllerQueryEvents = async (
  params?: EventsControllerQueryEventsParams,
  options?: RequestInit,
): Promise<eventsControllerQueryEventsResponse> => {
  return customFetch<eventsControllerQueryEventsResponse>(
    getEventsControllerQueryEventsUrl(params),
    {
      ...options,
      method: 'GET',
    },
  );
};

export const getEventsControllerQueryEventsQueryKey = (
  params?: EventsControllerQueryEventsParams,
) => {
  return [`/events`, ...(params ? [params] : [])] as const;
};

export const getEventsControllerQueryEventsQueryOptions = <
  TData = Awaited<ReturnType<typeof eventsControllerQueryEvents>>,
  TError = unknown,
>(
  params?: EventsControllerQueryEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerQueryEvents>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getEventsControllerQueryEventsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof eventsControllerQueryEvents>>> = ({
    signal,
  }) => eventsControllerQueryEvents(params, { signal, ...requestOptions });

  return { queryKey, queryFn, staleTime: 30000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof eventsControllerQueryEvents>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type EventsControllerQueryEventsQueryResult = NonNullable<
  Awaited<ReturnType<typeof eventsControllerQueryEvents>>
>;
export type EventsControllerQueryEventsQueryError = unknown;

export function useEventsControllerQueryEvents<
  TData = Awaited<ReturnType<typeof eventsControllerQueryEvents>>,
  TError = unknown,
>(
  params: undefined | EventsControllerQueryEventsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerQueryEvents>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerQueryEvents>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerQueryEvents>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerQueryEvents<
  TData = Awaited<ReturnType<typeof eventsControllerQueryEvents>>,
  TError = unknown,
>(
  params?: EventsControllerQueryEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerQueryEvents>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerQueryEvents>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerQueryEvents>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerQueryEvents<
  TData = Awaited<ReturnType<typeof eventsControllerQueryEvents>>,
  TError = unknown,
>(
  params?: EventsControllerQueryEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerQueryEvents>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Query affiliate events
 */

export function useEventsControllerQueryEvents<
  TData = Awaited<ReturnType<typeof eventsControllerQueryEvents>>,
  TError = unknown,
>(
  params?: EventsControllerQueryEventsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerQueryEvents>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getEventsControllerQueryEventsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieve a single affiliate event by its UUID
 * @summary Get event by ID
 */
export type eventsControllerGetEventByIdResponse200 = {
  data: void;
  status: 200;
};

export type eventsControllerGetEventByIdResponse404 = {
  data: void;
  status: 404;
};

export type eventsControllerGetEventByIdResponseSuccess =
  eventsControllerGetEventByIdResponse200 & {
    headers: Headers;
  };
export type eventsControllerGetEventByIdResponseError = eventsControllerGetEventByIdResponse404 & {
  headers: Headers;
};

export type eventsControllerGetEventByIdResponse =
  | eventsControllerGetEventByIdResponseSuccess
  | eventsControllerGetEventByIdResponseError;

export const getEventsControllerGetEventByIdUrl = (id: string) => {
  return `/events/${id}`;
};

export const eventsControllerGetEventById = async (
  id: string,
  options?: RequestInit,
): Promise<eventsControllerGetEventByIdResponse> => {
  return customFetch<eventsControllerGetEventByIdResponse>(getEventsControllerGetEventByIdUrl(id), {
    ...options,
    method: 'GET',
  });
};

export const getEventsControllerGetEventByIdQueryKey = (id?: string) => {
  return [`/events/${id}`] as const;
};

export const getEventsControllerGetEventByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof eventsControllerGetEventById>>,
  TError = void,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetEventById>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getEventsControllerGetEventByIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof eventsControllerGetEventById>>> = ({
    signal,
  }) => eventsControllerGetEventById(id, { signal, ...requestOptions });

  return { queryKey, queryFn, enabled: !!id, staleTime: 30000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof eventsControllerGetEventById>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type EventsControllerGetEventByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof eventsControllerGetEventById>>
>;
export type EventsControllerGetEventByIdQueryError = void;

export function useEventsControllerGetEventById<
  TData = Awaited<ReturnType<typeof eventsControllerGetEventById>>,
  TError = void,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetEventById>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerGetEventById>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerGetEventById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerGetEventById<
  TData = Awaited<ReturnType<typeof eventsControllerGetEventById>>,
  TError = void,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetEventById>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerGetEventById>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerGetEventById>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerGetEventById<
  TData = Awaited<ReturnType<typeof eventsControllerGetEventById>>,
  TError = void,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetEventById>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get event by ID
 */

export function useEventsControllerGetEventById<
  TData = Awaited<ReturnType<typeof eventsControllerGetEventById>>,
  TError = void,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetEventById>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getEventsControllerGetEventByIdQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieve aggregated statistics for events by brand, including time-series data
 * @summary Get event statistics
 */
export type eventsControllerGetStatsResponse200 = {
  data: void;
  status: 200;
};

export type eventsControllerGetStatsResponseSuccess = eventsControllerGetStatsResponse200 & {
  headers: Headers;
};
export type eventsControllerGetStatsResponse = eventsControllerGetStatsResponseSuccess;

export const getEventsControllerGetStatsUrl = (params?: EventsControllerGetStatsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/events/analytics/stats?${stringifiedParams}`
    : `/events/analytics/stats`;
};

export const eventsControllerGetStats = async (
  params?: EventsControllerGetStatsParams,
  options?: RequestInit,
): Promise<eventsControllerGetStatsResponse> => {
  return customFetch<eventsControllerGetStatsResponse>(getEventsControllerGetStatsUrl(params), {
    ...options,
    method: 'GET',
  });
};

export const getEventsControllerGetStatsQueryKey = (params?: EventsControllerGetStatsParams) => {
  return [`/events/analytics/stats`, ...(params ? [params] : [])] as const;
};

export const getEventsControllerGetStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof eventsControllerGetStats>>,
  TError = unknown,
>(
  params?: EventsControllerGetStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetStats>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getEventsControllerGetStatsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof eventsControllerGetStats>>> = ({
    signal,
  }) => eventsControllerGetStats(params, { signal, ...requestOptions });

  return { queryKey, queryFn, staleTime: 30000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof eventsControllerGetStats>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type EventsControllerGetStatsQueryResult = NonNullable<
  Awaited<ReturnType<typeof eventsControllerGetStats>>
>;
export type EventsControllerGetStatsQueryError = unknown;

export function useEventsControllerGetStats<
  TData = Awaited<ReturnType<typeof eventsControllerGetStats>>,
  TError = unknown,
>(
  params: undefined | EventsControllerGetStatsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetStats>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerGetStats>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerGetStats>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerGetStats<
  TData = Awaited<ReturnType<typeof eventsControllerGetStats>>,
  TError = unknown,
>(
  params?: EventsControllerGetStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetStats>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerGetStats>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerGetStats>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerGetStats<
  TData = Awaited<ReturnType<typeof eventsControllerGetStats>>,
  TError = unknown,
>(
  params?: EventsControllerGetStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetStats>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get event statistics
 */

export function useEventsControllerGetStats<
  TData = Awaited<ReturnType<typeof eventsControllerGetStats>>,
  TError = unknown,
>(
  params?: EventsControllerGetStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetStats>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getEventsControllerGetStatsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Compare metrics (clicks, regs, ftds, cpas, deps, redeps) between two time periods. Supports day-to-day, week-to-week, month-to-month, and custom date comparisons.
 * @summary Compare statistics between two periods
 */
export type eventsControllerCompareStatsResponse200 = {
  data: void;
  status: 200;
};

export type eventsControllerCompareStatsResponseSuccess =
  eventsControllerCompareStatsResponse200 & {
    headers: Headers;
  };
export type eventsControllerCompareStatsResponse = eventsControllerCompareStatsResponseSuccess;

export const getEventsControllerCompareStatsUrl = (params: EventsControllerCompareStatsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/events/analytics/compare?${stringifiedParams}`
    : `/events/analytics/compare`;
};

export const eventsControllerCompareStats = async (
  params: EventsControllerCompareStatsParams,
  options?: RequestInit,
): Promise<eventsControllerCompareStatsResponse> => {
  return customFetch<eventsControllerCompareStatsResponse>(
    getEventsControllerCompareStatsUrl(params),
    {
      ...options,
      method: 'GET',
    },
  );
};

export const getEventsControllerCompareStatsQueryKey = (
  params?: EventsControllerCompareStatsParams,
) => {
  return [`/events/analytics/compare`, ...(params ? [params] : [])] as const;
};

export const getEventsControllerCompareStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof eventsControllerCompareStats>>,
  TError = unknown,
>(
  params: EventsControllerCompareStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerCompareStats>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getEventsControllerCompareStatsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof eventsControllerCompareStats>>> = ({
    signal,
  }) => eventsControllerCompareStats(params, { signal, ...requestOptions });

  return { queryKey, queryFn, staleTime: 30000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof eventsControllerCompareStats>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type EventsControllerCompareStatsQueryResult = NonNullable<
  Awaited<ReturnType<typeof eventsControllerCompareStats>>
>;
export type EventsControllerCompareStatsQueryError = unknown;

export function useEventsControllerCompareStats<
  TData = Awaited<ReturnType<typeof eventsControllerCompareStats>>,
  TError = unknown,
>(
  params: EventsControllerCompareStatsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerCompareStats>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerCompareStats>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerCompareStats>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerCompareStats<
  TData = Awaited<ReturnType<typeof eventsControllerCompareStats>>,
  TError = unknown,
>(
  params: EventsControllerCompareStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerCompareStats>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerCompareStats>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerCompareStats>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerCompareStats<
  TData = Awaited<ReturnType<typeof eventsControllerCompareStats>>,
  TError = unknown,
>(
  params: EventsControllerCompareStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerCompareStats>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Compare statistics between two periods
 */

export function useEventsControllerCompareStats<
  TData = Awaited<ReturnType<typeof eventsControllerCompareStats>>,
  TError = unknown,
>(
  params: EventsControllerCompareStatsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerCompareStats>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getEventsControllerCompareStatsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Download events as a CSV file for the specified brand and date range
 * @summary Export events to CSV
 */
export type eventsControllerExportCsvResponse200 = {
  data: void;
  status: 200;
};

export type eventsControllerExportCsvResponseSuccess = eventsControllerExportCsvResponse200 & {
  headers: Headers;
};
export type eventsControllerExportCsvResponse = eventsControllerExportCsvResponseSuccess;

export const getEventsControllerExportCsvUrl = (params?: EventsControllerExportCsvParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/events/analytics/export?${stringifiedParams}`
    : `/events/analytics/export`;
};

export const eventsControllerExportCsv = async (
  params?: EventsControllerExportCsvParams,
  options?: RequestInit,
): Promise<eventsControllerExportCsvResponse> => {
  return customFetch<eventsControllerExportCsvResponse>(getEventsControllerExportCsvUrl(params), {
    ...options,
    method: 'GET',
  });
};

export const getEventsControllerExportCsvQueryKey = (params?: EventsControllerExportCsvParams) => {
  return [`/events/analytics/export`, ...(params ? [params] : [])] as const;
};

export const getEventsControllerExportCsvQueryOptions = <
  TData = Awaited<ReturnType<typeof eventsControllerExportCsv>>,
  TError = unknown,
>(
  params?: EventsControllerExportCsvParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerExportCsv>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getEventsControllerExportCsvQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof eventsControllerExportCsv>>> = ({
    signal,
  }) => eventsControllerExportCsv(params, { signal, ...requestOptions });

  return { queryKey, queryFn, staleTime: 30000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof eventsControllerExportCsv>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type EventsControllerExportCsvQueryResult = NonNullable<
  Awaited<ReturnType<typeof eventsControllerExportCsv>>
>;
export type EventsControllerExportCsvQueryError = unknown;

export function useEventsControllerExportCsv<
  TData = Awaited<ReturnType<typeof eventsControllerExportCsv>>,
  TError = unknown,
>(
  params: undefined | EventsControllerExportCsvParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerExportCsv>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerExportCsv>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerExportCsv>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerExportCsv<
  TData = Awaited<ReturnType<typeof eventsControllerExportCsv>>,
  TError = unknown,
>(
  params?: EventsControllerExportCsvParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerExportCsv>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerExportCsv>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerExportCsv>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerExportCsv<
  TData = Awaited<ReturnType<typeof eventsControllerExportCsv>>,
  TError = unknown,
>(
  params?: EventsControllerExportCsvParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerExportCsv>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export events to CSV
 */

export function useEventsControllerExportCsv<
  TData = Awaited<ReturnType<typeof eventsControllerExportCsv>>,
  TError = unknown,
>(
  params?: EventsControllerExportCsvParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerExportCsv>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getEventsControllerExportCsvQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieve health status per source with lag times and today's event counts
 * @summary Get analytics health status
 */
export type eventsControllerGetAnalyticsHealthResponse200 = {
  data: AnalyticsHealthResponseDto;
  status: 200;
};

export type eventsControllerGetAnalyticsHealthResponseSuccess =
  eventsControllerGetAnalyticsHealthResponse200 & {
    headers: Headers;
  };
export type eventsControllerGetAnalyticsHealthResponse =
  eventsControllerGetAnalyticsHealthResponseSuccess;

export const getEventsControllerGetAnalyticsHealthUrl = (
  params?: EventsControllerGetAnalyticsHealthParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : value.toString());
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/events/analytics/health?${stringifiedParams}`
    : `/events/analytics/health`;
};

export const eventsControllerGetAnalyticsHealth = async (
  params?: EventsControllerGetAnalyticsHealthParams,
  options?: RequestInit,
): Promise<eventsControllerGetAnalyticsHealthResponse> => {
  return customFetch<eventsControllerGetAnalyticsHealthResponse>(
    getEventsControllerGetAnalyticsHealthUrl(params),
    {
      ...options,
      method: 'GET',
    },
  );
};

export const getEventsControllerGetAnalyticsHealthQueryKey = (
  params?: EventsControllerGetAnalyticsHealthParams,
) => {
  return [`/events/analytics/health`, ...(params ? [params] : [])] as const;
};

export const getEventsControllerGetAnalyticsHealthQueryOptions = <
  TData = Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>,
  TError = unknown,
>(
  params?: EventsControllerGetAnalyticsHealthParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getEventsControllerGetAnalyticsHealthQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>> = ({
    signal,
  }) => eventsControllerGetAnalyticsHealth(params, { signal, ...requestOptions });

  return { queryKey, queryFn, staleTime: 30000, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type EventsControllerGetAnalyticsHealthQueryResult = NonNullable<
  Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>
>;
export type EventsControllerGetAnalyticsHealthQueryError = unknown;

export function useEventsControllerGetAnalyticsHealth<
  TData = Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>,
  TError = unknown,
>(
  params: undefined | EventsControllerGetAnalyticsHealthParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerGetAnalyticsHealth<
  TData = Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>,
  TError = unknown,
>(
  params?: EventsControllerGetAnalyticsHealthParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>,
          TError,
          Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEventsControllerGetAnalyticsHealth<
  TData = Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>,
  TError = unknown,
>(
  params?: EventsControllerGetAnalyticsHealthParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get analytics health status
 */

export function useEventsControllerGetAnalyticsHealth<
  TData = Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>,
  TError = unknown,
>(
  params?: EventsControllerGetAnalyticsHealthParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof eventsControllerGetAnalyticsHealth>>, TError, TData>
    >;
    request?: SecondParameter<typeof customFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getEventsControllerGetAnalyticsHealthQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
